'use client';

import React, { useCallback } from 'react';
import { useAuth } from 'src/context/auth';
import { useSite } from 'src/context/providers/SiteProvider';
import { faUserNinja } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/navigation';
import useUserImpersonation from 'src/hooks/users/useUserImpersonation';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';

interface ImpersonateButtonProps {
  userId: string;
  variant?: 'regular' | 'compact' | 'icon';
  /**
   * The user being impersonated, when the caller has it (e.g. a user table
   * row). Without it the label only knows the company admin's `currentUser`
   * and reads "Support" for anyone else.
   */
  user?: MedScout.User;
}

const ImpersonateButton = ({
  userId,
  variant = 'regular',
  user: targetUser,
}: ImpersonateButtonProps) => {
  const { user } = useAuth();
  const router = useRouter();
  const { state } = useSite();
  const { currentUser } = state.companyAdmin;

  const { impersonate } = useUserImpersonation();

  const isMedScoutUser = user?.is_superuser;

  const handleImpersonate = useCallback(
    async (id: string | number) => {
      await impersonate(Number(id));
      router.push('/search');
    },
    [impersonate, router]
  );

  if (!isMedScoutUser) return null;

  const namedUser =
    targetUser ??
    (currentUser?.id?.toString() === userId ? currentUser : undefined);

  const buttonText = namedUser
    ? [namedUser.first_name, namedUser.last_name].filter(Boolean).join(' ') ||
      'User'
    : 'Support';

  const buttonTitle = `Impersonate ${buttonText}`;

  switch (variant) {
    case 'regular': {
      return (
        <PulseButton
          size="sm"
          variant="outline"
          color="primary"
          onClick={() => handleImpersonate(userId)}
          startDecorator={faUserNinja}
          disabled={!isMedScoutUser || !userId}
          className="rounded-full w-24"
          aria-label={buttonTitle}
          data-testid="impersonate-button"
        >
          {buttonText}
        </PulseButton>
      );
    }

    case 'compact': {
      return (
        <PulseIconButton
          size="sm"
          variant="soft"
          color="primary"
          onClick={() => handleImpersonate(userId)}
          icon={faUserNinja}
          disabled={!isMedScoutUser || !userId}
          aria-label={buttonTitle}
          tooltip={buttonTitle}
          data-testid="impersonate-button-compact"
        />
      );
    }

    case 'icon': {
      return (
        <PulseIconButton
          size="sm"
          variant="plain"
          onClick={() => handleImpersonate(userId)}
          icon={faUserNinja}
          disabled={!isMedScoutUser || !userId}
          aria-label={buttonTitle}
          tooltip={buttonTitle}
          className="text-neutral-500 hover:text-sky-500"
          data-testid="impersonate-button-icon"
        />
      );
    }

    default:
      return null;
  }
};

export default ImpersonateButton;
